<template>
    <div>
        <AppInputLabel class="mb-1" :name="name" :label="label" :hasError="hasError(name).value" :required="required" />

        <div>
            <VueTelInput
                :id="name"
                :name="name"
                v-model="localPhoneNumber"
                :autoFormat="true"
                mode="international"
                :validCharactersOnly="true"
                class="w-full"
                :class="{ 'border-critial': hasError(name).value }"
                :inputOptions="{
                    showDialCode: true,
                    autofocus: true,
                    placeholder: placeholder,
                    styleClasses: 'w-full border rounded px-3 py-2'
                }"
                @country-changed="handleCountryCodeChanged"
                @validate="validateInput($event)"
                @update:modelValue="$emit('update:modelValue', $event)"
            />
        </div>

        <AppInputError v-show="hasError(name)" :error="errors.get(name)" />
    </div>
</template>
<script setup lang="ts">
import { CountryMeta } from "@/models/CountryMeta";
import { PhoneMeta } from "@/models/PhoneMeta";
import { nextTick, inject, ref, watch, defineProps, defineEmits, onMounted, onUnmounted } from "vue";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";
import useFormValidation from "@/modules/useFormValidation";
import usePhone from "@/modules/usePhone";
import { ValidationContextKey } from "@/models/Context";
import AppInputLabel from "./AppInputLabel.vue";
import AppInputError from "./AppInputError.vue";

// Define props
const props = defineProps<{
    label: string;
    name: string;
    modelValue: string;
    placeholder?: string;
    required?: boolean;
}>();

// Define emits
const emit = defineEmits<{
    (e: "update:modelValue", value: string): void;
    (e: "validate", event: PhoneMeta): void;
}>();


// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { hasError, errors, clearError, registerValidator } = useFormValidation(context);
const initialized = ref(false);
const { sanitizePhoneNumber, validatePhoneInput, validatePhoneHasValue } = usePhone(errors);
// Reactive reference for the formatted phone number
const localPhoneNumber = ref(props.modelValue);
const localPhoneMeta = ref<PhoneMeta|undefined>();

const handleCountryCodeChanged = (contryObject: CountryMeta) => {
    // Emit the validate event
    if (localPhoneNumber.value && !localPhoneNumber.value.startsWith(`+${contryObject.dialCode}`)) {
        localPhoneNumber.value = `+${contryObject.dialCode} `
        emit("update:modelValue", localPhoneNumber.value);
    }
};

const validatePhone = () => {
    validatePhoneHasValue(localPhoneNumber.value);
    if(localPhoneMeta.value && localPhoneMeta.value.valid !== undefined) {
        validatePhoneInput(localPhoneMeta.value);
    }
};

registerValidator(props.name, validatePhone);

const validateInput = (phoneMeta: PhoneMeta) => {
    clearError(props.name);
    localPhoneMeta.value = phoneMeta;
    if (!initialized.value) {
        console.log("not initialized");
        return;
    }
    if (props.required) {
        validatePhoneHasValue(phoneMeta.number);
    }

    console.log("hasError:", errors.get(props.name));
    if (!hasError(props.name).value) {
        validatePhoneInput(phoneMeta);
    }
};


// Watch for changes in the modelValue prop
watch(() => props.modelValue, (newValue) => {
    if (localPhoneNumber.value !== newValue) {
        localPhoneNumber.value = newValue;
    }
});

onMounted(() => {
    initialized.value = false;
    clearError(props.name);
    nextTick(() => {
        setTimeout(() => {
            console.log("onMounted");
            initialized.value = true;
        }, 1000);
    });
});

onUnmounted(() => {
    console.log("onUnmounted:", props.name);
    clearError(props.name);
});

</script>

<style >
/* Override vue-tel-input default styles to match your design */
:deep(.vue-tel-input) {
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
}

:deep(.vue-tel-input:focus-within) {
    border-color: var(--color-action);
}

:deep(.vue-tel-input input) {
    border: 0;
    width: 100%;
    padding: 0.75rem 1rem;
}

:deep(.vti__dropdown) {
    border: 0;
    padding: 0.75rem 1rem;
}

:deep(.vti__dropdown.open) {
    border-color: var(--color-action);
}
</style>
